import "../css/ClusterListings.css";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { Helmet } from "react-helmet";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { BootstrapOutlineButton } from "components/buttons/CustomButtons";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router";
import {
  AzureScriptDto,
  GcpScriptDto,
  CustomerCftDto,
  CustomerEnvironment,
  EnvironmentTypeOption,
  environmentTypeOptions as environmentTypes,
  environmentTypeOptions,
  isEligibleParentCluster,
  ProvisionRequest,
  ProvisionableSku,
} from "../utils/saastypes";
import LinearProgress from "../components/LinearProgress";
import AuthorizeAccountDialog from "../components/dialogs/AuthorizeAccountDialog";
import {
  IdTitle,
  useActionWithPath,
  useClusterListing,
  useFixedDomains,
  useGcpVpcs,
  useValidateGcpVpc,
} from "utils/query";
import {
  getComputeUnitIcon,
  getEnvironmentIcon,
  getValueForAutocomplete,
  isValidClusterName,
} from "utils/utils";
import ClusterField from "../components/ClusterField";
import { useAuthMachine } from "auth/state/hook";
import { useProvisionableSkus } from "../utils/query/loggedHooks";
import StyledMainButton from "../components/buttons/StyledMainButton";

import awsLogo from "../images/aws-logo.svg";
import azureLogo from "../images/azure-logo.svg";
import gcloudLogo from "../images/gcloud-logo.svg";
import LogoSelector from "../components/LogoSelector";
import { useAuth } from "auth/AuthContext";
import { MAX_CLUSTER_NAME_SIZE } from "constants/common";
import AzureAuthorizeAccountDialog from "components/dialogs/AzureAuthorizeAccountDialog";
import GcpAuthorizeAccountDialog from "components/dialogs/GcpAuthorizeAccountDialog";
import { green } from "@mui/material/colors";
import { gcpSupportedRegions } from "constants/gcpRegions";

const SUB_DOMAIN_REGEX = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/m;

// Ingress type options (same as AWS)
const gcpIngressTypes = [
  { id: "INTERNAL", title: "VPC Only - Access via VPN or Peering" },
  { id: "EXTERNAL", title: "Internet - Access using Credentials" },
];

// GCP zones suffix — most GCP regions have a, b, c zones
const GCP_ZONE_SUFFIXES = ["a", "b", "c"];

// OCU (Orkes Compute Unit) options for GCP cluster sizing
const gcpOcuOptions = [
  {
    id: "1",
    title: "Small - 100s - 1000s of Workflows / Day",
    description:
      "GKE: e2-standard-4 (1-2 nodes/zone) | Redis: 6 GB HA | Postgres: 2 vCPU, 512 GB",
    machineType: "e2-standard-4",
    minNodes: 1,
    maxNodes: 2,
    redisGb: 6,
    postgresTier: "db-custom-2-7680",
    postgresDisk: 512,
  },
  {
    id: "2",
    title: "Medium - 1000s - 10,000s of Workflows / Day",
    description:
      "GKE: e2-standard-8 (2-3 nodes/zone) | Redis: 12 GB HA | Postgres: 4 vCPU, 1 TB",
    machineType: "e2-standard-8",
    minNodes: 2,
    maxNodes: 3,
    redisGb: 12,
    postgresTier: "db-custom-4-15360",
    postgresDisk: 1024,
  },
  {
    id: "3",
    title: "Large - 100,000s - 1,000,000s of Workflows / Day",
    description:
      "GKE: e2-standard-8 (3-4 nodes/zone) | Redis: 16 GB HA | Postgres: 4 vCPU, 1 TB",
    machineType: "e2-standard-8",
    minNodes: 3,
    maxNodes: 4,
    redisGb: 16,
    postgresTier: "db-custom-4-26624",
    postgresDisk: 1024,
  },
  {
    id: "4",
    title: "Extra Large - > 1,000,000s of Workflows / Day",
    description:
      "GKE: n2-standard-8 (3-5 nodes/zone) | Redis: 24 GB HA | Postgres: 8 vCPU, 2 TB",
    machineType: "n2-standard-8",
    minNodes: 3,
    maxNodes: 5,
    redisGb: 24,
    postgresTier: "db-custom-8-30720",
    postgresDisk: 2048,
  },
  {
    id: "5",
    title: "Optimized for High Performance",
    description:
      "GKE: n2-standard-16 (4-6 nodes/zone) | Redis: 32 GB HA | Postgres: 16 vCPU, 4 TB",
    machineType: "n2-standard-16",
    minNodes: 4,
    maxNodes: 6,
    redisGb: 32,
    postgresTier: "db-custom-16-61440",
    postgresDisk: 4096,
  },
];

const validateClusterName = (clusterName: string | undefined) => {
  if (!clusterName) {
    return "Cluster name is required";
  } else if (!isValidClusterName(clusterName)) {
    return clusterName.startsWith("-")
      ? "Cluster name cannot start with a hyphen"
      : "Cluster name cannot contain special characters. Only alphanumeric and hyphen allowed";
  }
  return "";
};

// The worker environment's name defaults to the parent's, because the customer is attaching
// workers to a cluster rather than naming a new one, and the backend still requires a valid
// unique clusterName (@NotBlank @ValidClusterName @Size(max=MAX_CLUSTER_NAME_SIZE) on
// ProvisionDedicatedRequestDto). It is a default rather than a fixed derivation: names are
// unique per organisation and clusterExists ignores state, so a read-only derivation allowed
// exactly one worker environment per parent for all time, even after one was deleted.
const WORKER_ENVIRONMENT_NAME_SUFFIX = "-wk";
// The derived name has to fit MAX_CLUSTER_NAME_SIZE, so the parent name is TRUNCATED
// rather than rejected. Uniqueness does not depend on the full parent name — the control
// plane appends its own unique characters downstream — and rejecting every parent longer
// than 15 chars locked out most realistic cluster names (e.g. "az-orkes-internal").
const WORKER_ENVIRONMENT_PARENT_NAME_LIMIT =
  MAX_CLUSTER_NAME_SIZE - WORKER_ENVIRONMENT_NAME_SUFFIX.length;
const workerEnvironmentName = (parentClusterName: string | undefined) => {
  const parent = parentClusterName?.trim();
  if (!parent) {
    return undefined;
  }
  // Trailing hyphens are stripped so a cut that lands on one cannot yield "…-qu--wk"
  const truncated = parent
    .slice(0, WORKER_ENVIRONMENT_PARENT_NAME_LIMIT)
    .replace(/-+$/, "");
  return truncated
    ? `${truncated}${WORKER_ENVIRONMENT_NAME_SUFFIX}`
    : undefined;
};

// Validate CIDR format and range for GCP VPC
const validateGcpVpcCidr = (cidr: string | undefined): string => {
  if (!cidr || cidr.trim() === "") return ""; // optional — will default to 10.0.0.0/16
  const cidrRegex = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/(\d{1,2})$/;
  const match = cidr.match(cidrRegex);
  if (!match)
    return "Invalid CIDR format. Expected: x.x.x.x/y (e.g., 10.0.0.0/16)";
  const parts = match.map(Number);
  const s1 = parts[1];
  const s2 = parts[2];
  const s4 = parts[4];
  const mask = parts[5];

  // Validate RFC1918 private IP ranges
  const isPrivate =
    s1 === 10 ||
    (s1 === 172 && s2 >= 16 && s2 <= 31) ||
    (s1 === 192 && s2 === 168);
  if (!isPrivate)
    return "VPC CIDR must be a private IP range (10.x.x.x, 172.16-31.x.x, or 192.168.x.x)";

  // Validate mask range /16 to /24
  if (mask < 16 || mask > 24)
    return "VPC CIDR mask must be between /16 and /24";

  // Last octet should be 0
  if (s4 !== 0) return "VPC CIDR should end with .0 (e.g., 10.0.0.0/16)";

  return "";
};

const validateGcpPodsCidr = (cidr: string | undefined): string => {
  if (!cidr || cidr.trim() === "") return ""; // optional — will default to 10.128.0.0/17
  const cidrRegex = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/(\d{1,2})$/;
  const match = cidr.match(cidrRegex);
  if (!match)
    return "Invalid CIDR format. Expected: x.x.x.x/y (e.g., 10.128.0.0/17)";
  const parts = match.map(Number);
  const s1 = parts[1];
  const s2 = parts[2];
  const mask = parts[5];

  const isPrivate =
    s1 === 10 ||
    (s1 === 172 && s2 >= 16 && s2 <= 31) ||
    (s1 === 192 && s2 === 168);
  if (!isPrivate)
    return "Pods CIDR must be a private IP range (10.x.x.x, 172.16-31.x.x, or 192.168.x.x)";

  if (mask < 8 || mask > 24)
    return "Pods CIDR mask must be between /8 and /24. Smaller than /24 risks insufficient pod IPs.";

  return "";
};

const validateGcpPscPrefixLength = (val: number | undefined): string => {
  if (val === undefined || val === null) return ""; // optional — defaults to 16
  if (val < 16 || val > 24)
    return "PSC prefix length must be between 16 (/16 block, default) and 24 (/24 block, smallest allowed)";
  return "";
};

const validateDataForCft = (
  provisionRequest: ProvisionRequest,
  errors: any,
  setErrors: any
): boolean => {
  const newErrors: any = {};
  if (
    undefined === provisionRequest.customerAccountId ||
    provisionRequest.customerAccountId?.trim() === ""
  ) {
    newErrors["customerAccountId"] = "Cloud provider account id cannot be null";
  }
  const clusterNameError = validateClusterName(provisionRequest.clusterName);
  if (clusterNameError) {
    newErrors["clusterName"] = clusterNameError;
  }
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

/**
 * Smallest vnet an OCU SKU cluster can be carved from. ClusterWorkflowService splits the vnet into
 * a /21 main subnet and a /21 database subnet, which a /20 does exactly. Mirrors the server-side
 * rule in ClusterController so the user sees it here rather than after submitting.
 */
const SKU_MAX_VNET_MASK = 20;

const validateDataForAzure = (
  provisionRequest: ProvisionRequest,
  errors: any,
  setErrors: any,
  isWorkerHosting?: boolean,
  azureSizingMode: "legacy" | "sku" = "legacy",
  azureSelectedSku: { skuName: string } | null = null
): boolean => {
  const newErrors: any = {};
  if (azureSizingMode === "sku") {
    if (!azureSelectedSku) {
      newErrors["skuName"] = "A SKU is required";
    }
    // Azure CNI pre-reserves maxPods+1 addresses per node whether or not pods exist, so a SKU
    // cluster needs a /21 main subnet -- far more than the /24 a legacy cluster is carved.
    const vnetCidr = provisionRequest.azureVnetCidr;
    const prefix = vnetCidr?.includes("/")
      ? parseInt(vnetCidr.split("/")[1], 10)
      : NaN;
    if (!vnetCidr || Number.isNaN(prefix)) {
      newErrors["azureVnetCidr"] = "A VNet CIDR is required for a SKU cluster";
    } else if (prefix > SKU_MAX_VNET_MASK) {
      newErrors[
        "azureVnetCidr"
      ] = `A SKU cluster needs a VNet of /${SKU_MAX_VNET_MASK} or larger to carve its /21 subnets`;
    }
  }
  if (
    undefined === provisionRequest.azureResourceGroup ||
    provisionRequest.azureResourceGroup?.trim() === ""
  ) {
    newErrors["azureResourceGroup"] = "Azure resource group cannot be null";
  }
  // Worker hosting attaches to an existing cluster, so the parent is required too. The
  // environment name is validated on both paths: it is now an editable field seeded from the
  // parent rather than a read-only derivation, so it can be blank, too long or malformed just
  // like any other cluster name, and the error has a field to land on.
  if (isWorkerHosting) {
    if (
      undefined === provisionRequest.parentClusterId ||
      provisionRequest.parentClusterId?.trim() === ""
    ) {
      newErrors["parentClusterId"] =
        "Select the cluster these workers attach to";
    }
  }
  const clusterNameError = validateClusterName(provisionRequest.clusterName);
  if (clusterNameError) {
    newErrors["clusterName"] = clusterNameError;
  }
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

const validateDataForGcp = (
  provisionRequest: ProvisionRequest,
  errors: any,
  setErrors: any,
  useSharedVpc?: boolean,
  gcpSizingMode: "legacy" | "sku" = "legacy",
  gcpSelectedSku: { skuName: string } | null = null
): boolean => {
  const newErrors: any = {};
  if (gcpSizingMode === "sku" && !gcpSelectedSku) {
    newErrors["skuName"] = "A SKU is required";
  }
  if (
    undefined === provisionRequest.gcpProjectId ||
    provisionRequest.gcpProjectId?.trim() === ""
  ) {
    newErrors["gcpProjectId"] = "GCP Project ID cannot be null";
  }
  const clusterNameError = validateClusterName(provisionRequest.clusterName);
  if (clusterNameError) {
    newErrors["clusterName"] = clusterNameError;
  }
  // Validate VPC CIDR only for standard modes — Shared VPC skips CIDR entirely
  if (!useSharedVpc) {
    const cidrError = validateGcpVpcCidr(provisionRequest.gcpVpcCidr);
    if (cidrError) {
      newErrors["gcpVpcCidr"] = cidrError;
    }
    const podsCidrError = validateGcpPodsCidr(provisionRequest.gcpPodsCidr);
    if (podsCidrError) {
      newErrors["gcpPodsCidr"] = podsCidrError;
    }
    const pscError = validateGcpPscPrefixLength(
      provisionRequest.gcpPscPrefixLength
    );
    if (pscError) {
      newErrors["gcpPscPrefixLength"] = pscError;
    }
  }
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

const azureSupportedRegions: IdTitle[] = [
  { id: "eastus", title: "East US" },
  { id: "eastus2", title: "East US 2" },
  { id: "centralus", title: "Central US" },
  { id: "southcentralus", title: "South Central US" },
  { id: "westus3", title: "West US 3" },
  { id: "australiaeast", title: "Australia East (Sydney)" },
  { id: "francecentral", title: "Central Europe (Paris)" },
  { id: "northeurope", title: "North Europe" },
  { id: "westeurope", title: "West Europe" },
  { id: "qatarcentral", title: "Qatar" },
  { id: "uksouth", title: "UK South" },
  { id: "canadacentral", title: "Canada Central" },
  { id: "canadaeast", title: "Canada East" },
  { id: "germanywestcentral", title: "Germany West Central" },
  { id: "uaenorth", title: "UAE North" },
];

type AzureSkuOption = ProvisionableSku & { id: string };
type GcpSkuOption = ProvisionableSku & { id: string };

export default function CreateNewEnvironmentCustomCluster() {
  const navigate = useNavigate();
  const { setErrorToast, authService } = useAuth();
  const [{ isOrkesUser }] = useAuthMachine(authService);

  const [provisionRequest, setProvisionRequest] = useState<ProvisionRequest>(
    new ProvisionRequest("AWS")
  );
  const [errors, setErrors] = useState<any>({});

  // OCU SKU sizing for Azure. Legacy computeUnits stays the default, so nothing changes for
  // anyone who does not opt in. The AWS equivalent lives on step 2, which the Azure flow never
  // reaches -- verifyAuthActionAzure keeps the user on this page.
  const [azureSizingMode, setAzureSizingMode] = useState<"legacy" | "sku">(
    "legacy"
  );
  const [azureSelectedSku, setAzureSelectedSku] =
    useState<AzureSkuOption | null>(null);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  // Customer-owned networking: the customer builds the VPC and Orkes is granted no permission
  // to create, modify or delete any of it. About permissions, not topology -- selecting your
  // own subnets already works without this, and this is equally applicable to a VPC with an
  // internet gateway. A transit-gateway-only VPC needs it; it is not limited to one.
  //
  // Chosen here rather than on the next page because it decides which CloudFormation template
  // is generated, and that happens when Authorize Orkes is clicked. It cannot be changed
  // afterwards for this cluster name: the two templates grant different permissions under the
  // same role name and external id, so the server refuses to regenerate with a different value.
  const [byon, setByon] = useState(false);
  const [cftData, setCftData] = useState<CustomerCftDto>({});
  const [azureScriptData, setAzureScriptData] = useState<AzureScriptDto>({});
  const [gcpScriptData, setGcpScriptData] = useState<GcpScriptDto>({});
  // Worker Hosting (CLOUD-1193) — Azure only for now. Declared above the SKU state because
  // SKU sizing is a cluster-provisioning choice and must be switched off for worker hosting.
  const [azureDeploymentKind, setAzureDeploymentKind] = useState<
    "cluster" | "worker"
  >("cluster");
  const isAzureWorkerHosting = azureDeploymentKind === "worker";
  // The backend applies the @orkes.io restriction itself; this only avoids a pointless request.
  // Worker hosting attaches to an existing cluster and never carves infrastructure of its own,
  // so a SKU is meaningless there -- excluding it here also keeps activeAzureSku null, so
  // validateDataForAzure does not demand a SKU on the "Attach To Cluster" flow.
  const azureSkuModeAvailable =
    isOrkesUser &&
    provisionRequest.cloudProvider === "AZURE" &&
    !isAzureWorkerHosting;
  const { data: azureProvisionableSkus } = useProvisionableSkus(
    azureSkuModeAvailable
  );
  const azureSkuItems: AzureSkuOption[] = useMemo(
    () =>
      (azureProvisionableSkus ?? []).map((sku) => ({
        ...sku,
        id: sku.skuName,
        title: `${sku.title} - ${sku.azCount} availability zones`,
      })),
    [azureProvisionableSkus]
  );
  const activeAzureSku =
    azureSkuModeAvailable && azureSizingMode === "sku"
      ? azureSelectedSku
      : null;

  const gcpSkuModeAvailable =
    isOrkesUser && provisionRequest.cloudProvider === "GCP";
  const [gcpSizingMode, setGcpSizingMode] = useState<"legacy" | "sku">(
    "legacy"
  );
  const [gcpSelectedSku, setGcpSelectedSku] = useState<GcpSkuOption | null>(
    null
  );
  const { data: gcpProvisionableSkus } =
    useProvisionableSkus(gcpSkuModeAvailable);
  const gcpSkuItems: GcpSkuOption[] = useMemo(
    () =>
      (gcpProvisionableSkus ?? []).map((sku) => ({
        ...sku,
        id: sku.skuName,
        title: sku.title,
      })),
    [gcpProvisionableSkus]
  );
  const activeGcpSku =
    gcpSkuModeAvailable && gcpSizingMode === "sku" ? gcpSelectedSku : null;

  const [clusterNameCheckTimeout, setClusterNameCheckTimeout] =
    useState<NodeJS.Timeout | null>();

  const [environmentType, setEnvironmentType] = useState<EnvironmentTypeOption>(
    environmentTypes[0]
  );
  const [enableApiGateway, setEnableApiGateway] = useState(false);
  const [gcpOcu, setGcpOcu] = useState(gcpOcuOptions[0]);
  const [gcpSubDomain, setGcpSubDomain] = useState<string>("");
  const [gcpBaseDomain, setGcpBaseDomain] = useState<any>(null);
  const [gcpSelectedAzs, setGcpSelectedAzs] = useState<any[]>([]);
  const [gcpIngressType, setGcpIngressType] = useState<any>(null);
  const [gcpVpcMode, setGcpVpcMode] = useState<"new" | "existing" | "shared">(
    "new"
  );
  const gcpUseExistingVpc = gcpVpcMode === "existing";
  const gcpUseSharedVpc = gcpVpcMode === "shared";

  const downloadSharedVpcScript = () => {
    // Sanitize all user-provided values before embedding in shell script.
    // Only allow characters safe for GCP resource names and SA email addresses.
    const sanitize = (v: string | undefined, fallback: string): string =>
      (v ?? fallback).replace(/[^a-zA-Z0-9._\-/@]/g, "");

    const hostProject = sanitize(
      provisionRequest.gcpHostProjectId,
      "<HOST_PROJECT_ID>"
    );
    const serviceProject = sanitize(
      provisionRequest.gcpProjectId,
      "<SERVICE_PROJECT_ID>"
    );
    const region = sanitize(provisionRequest.region, "us-central1");
    const clusterName = sanitize(
      provisionRequest.clusterName?.toLowerCase(),
      "my-cluster"
    );
    const saEmail = `orkes-saas-${clusterName}@${serviceProject}.iam.gserviceaccount.com`;

    // Derive VPC/subnet names from self-links if already filled in, else use defaults
    const vpcSelfLink = sanitize(provisionRequest.gcpExistingVpcSelfLink, "");
    const subnetSelfLink = sanitize(
      provisionRequest.gcpExistingSubnetSelfLink,
      ""
    );
    const vpcName = vpcSelfLink
      ? vpcSelfLink.split("/").pop()!
      : `${clusterName}-vpc`;
    const subnetName = subnetSelfLink
      ? subnetSelfLink.split("/").pop()!
      : `${clusterName}-subnet`;
    const skipVpc = !!(vpcSelfLink && subnetSelfLink);

    const script = `#!/usr/bin/env bash
# ===========================================================================
# Orkes Shared VPC Prerequisites Setup — auto-generated for cluster: ${clusterName}
#
# Run this script as an admin of your HOST project (${hostProject}).
# Requires gcloud CLI or GCP Cloud Shell.
# ===========================================================================
set -euo pipefail

HOST_PROJECT="${hostProject}"
SERVICE_PROJECT="${serviceProject}"
REGION="${region}"
VPC_NAME="${vpcName}"
SUBNET_NAME="${subnetName}"
ORKES_SA_EMAIL="${saEmail}"

# CIDR ranges — adjust if these conflict with your existing address space
SUBNET_CIDR="\${SUBNET_CIDR:-10.0.0.0/22}"      # GKE node IPs
PODS_CIDR="\${PODS_CIDR:-10.4.0.0/17}"           # GKE pod IPs (one per pod)
SERVICES_CIDR="\${SERVICES_CIDR:-10.200.0.0/22}" # Kubernetes service IPs
PSA_CIDR="\${PSA_CIDR:-10.201.0.0/20}"           # Cloud SQL + Redis (PSA)

echo ""
echo "Host project    : $HOST_PROJECT"
echo "Service project : $SERVICE_PROJECT"
echo "Region          : $REGION"
echo "VPC             : $VPC_NAME"
echo "Subnet          : $SUBNET_NAME"
echo "Orkes SA        : $ORKES_SA_EMAIL"
echo ""

# ── Service project number (for GKE service agent) ──────────────────────────
SERVICE_PROJECT_NUMBER=$(gcloud projects describe "$SERVICE_PROJECT" --format="value(projectNumber)")
GKE_AGENT="service-\${SERVICE_PROJECT_NUMBER}@container-engine-robot.iam.gserviceaccount.com"
GOOGLE_APIS_SA="\${SERVICE_PROJECT_NUMBER}@cloudservices.gserviceaccount.com"

${
  skipVpc
    ? `# ── Skipping VPC/subnet creation (self-links already provided) ─────────────`
    : `# ── Step 1: Enable Shared VPC ───────────────────────────────────────────────
echo "▶ Enabling Shared VPC on host project..."
gcloud compute shared-vpc enable "$HOST_PROJECT" 2>&1 || true

echo "▶ Attaching $SERVICE_PROJECT as Shared VPC service project..."
gcloud compute shared-vpc associated-projects add "$SERVICE_PROJECT" \\
  --host-project="$HOST_PROJECT" 2>&1 || true

# ── Step 2: Create VPC ───────────────────────────────────────────────────────
echo "▶ Creating VPC $VPC_NAME..."
gcloud compute networks create "$VPC_NAME" \\
  --project="$HOST_PROJECT" \\
  --subnet-mode=custom \\
  --bgp-routing-mode=regional 2>&1 || echo "(VPC already exists)"

# ── Step 3: Create subnet with secondary ranges ──────────────────────────────
echo "▶ Creating subnet $SUBNET_NAME..."
gcloud compute networks subnets create "$SUBNET_NAME" \\
  --project="$HOST_PROJECT" \\
  --network="$VPC_NAME" \\
  --region="$REGION" \\
  --range="$SUBNET_CIDR" \\
  --secondary-range="pods=\${PODS_CIDR},services=\${SERVICES_CIDR}" \\
  --enable-private-ip-google-access 2>&1 || echo "(subnet already exists)"

# ── Step 4: PSA for Cloud SQL + Redis ────────────────────────────────────────
echo "▶ Setting up Private Service Access..."
gcloud compute addresses create google-managed-services-"$VPC_NAME" \\
  --project="$HOST_PROJECT" --global \\
  --purpose=VPC_PEERING \\
  --addresses="\${PSA_CIDR%%/*}" \\
  --prefix-length="\${PSA_CIDR##*/}" \\
  --network="$VPC_NAME" 2>&1 || echo "(PSA range already exists)"

gcloud services vpc-peerings connect \\
  --project="$HOST_PROJECT" \\
  --service=servicenetworking.googleapis.com \\
  --ranges=google-managed-services-"$VPC_NAME" \\
  --network="$VPC_NAME" 2>&1 || echo "(PSA peering already exists)"

# ── Step 5: Cloud NAT ────────────────────────────────────────────────────────
echo "▶ Creating Cloud NAT..."
gcloud compute routers create "\${VPC_NAME}-router" \\
  --project="$HOST_PROJECT" --network="$VPC_NAME" --region="$REGION" 2>&1 || true
gcloud compute routers nats create "\${VPC_NAME}-nat" \\
  --project="$HOST_PROJECT" --router="\${VPC_NAME}-router" \\
  --region="$REGION" --auto-allocate-nat-external-ips \\
  --nat-all-subnet-ip-ranges 2>&1 || echo "(NAT already exists)"

# ── Step 6: Firewall rules ───────────────────────────────────────────────────
echo "▶ Creating firewall rules..."
gcloud compute firewall-rules create "\${VPC_NAME}-allow-internal" \\
  --project="$HOST_PROJECT" --network="$VPC_NAME" \\
  --allow=tcp,udp,icmp \\
  --source-ranges="$SUBNET_CIDR,$PODS_CIDR" 2>&1 || true

gcloud compute firewall-rules create "\${VPC_NAME}-allow-http-https" \\
  --project="$HOST_PROJECT" --network="$VPC_NAME" \\
  --allow=tcp:80,tcp:443 --source-ranges="0.0.0.0/0" \\
  --description="Required for Let's Encrypt cert renewal" 2>&1 || true`
}

# ── IAM grants ───────────────────────────────────────────────────────────────
echo "▶ Granting IAM permissions..."
gcloud compute networks subnets add-iam-policy-binding "$SUBNET_NAME" \\
  --project="$HOST_PROJECT" --region="$REGION" \\
  --member="serviceAccount:$ORKES_SA_EMAIL" \\
  --role="roles/compute.networkUser" > /dev/null

gcloud compute networks subnets add-iam-policy-binding "$SUBNET_NAME" \\
  --project="$HOST_PROJECT" --region="$REGION" \\
  --member="serviceAccount:$GKE_AGENT" \\
  --role="roles/compute.networkUser" > /dev/null

gcloud compute networks subnets add-iam-policy-binding "$SUBNET_NAME" \\
  --project="$HOST_PROJECT" --region="$REGION" \\
  --member="serviceAccount:$GOOGLE_APIS_SA" \\
  --role="roles/compute.networkUser" > /dev/null

gcloud projects add-iam-policy-binding "$HOST_PROJECT" \\
  --member="serviceAccount:$GKE_AGENT" \\
  --role="roles/container.hostServiceAgentUser" > /dev/null

gcloud projects add-iam-policy-binding "$HOST_PROJECT" \\
  --member="serviceAccount:$ORKES_SA_EMAIL" \\
  --role="roles/redis.admin" > /dev/null

echo ""
echo "✅ Setup complete! Use these values in the Orkes portal:"
echo ""
echo "  VPC Self-Link    : projects/$HOST_PROJECT/global/networks/$VPC_NAME"
echo "  Subnet Self-Link : projects/$HOST_PROJECT/regions/$REGION/subnetworks/$SUBNET_NAME"
echo "  Pod range name   : pods"
echo "  Service range    : services"
echo ""
`;

    const blob = new Blob([script], { type: "text/x-sh" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orkes-shared-vpc-setup-${clusterName}.sh`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const [gcpSelectedVpc, setGcpSelectedVpc] = useState<any>(null);

  // Fetch GCP VPCs (only when user opts for existing VPC)
  const { data: gcpVpcList, isFetching: gcpVpcsFetching } = useGcpVpcs(
    provisionRequest.clusterName?.toLowerCase(),
    provisionRequest.gcpProjectId,
    gcpUseExistingVpc && provisionRequest.gcpCredentialsValidated === true
  );

  const gcpVpcItems = useMemo(() => {
    if (!gcpVpcList) return [];
    return gcpVpcList.map((vpc: any) => ({
      id: vpc.name,
      title: `${vpc.name}${vpc.isEmpty ? "" : " (has subnets - not eligible)"}`,
      isEmpty: vpc.isEmpty,
      selfLink: vpc.selfLink,
    }));
  }, [gcpVpcList]);

  const {
    data: vpcValidation,
    isFetching: gcpVpcValidating,
    isError: gcpVpcValidateError,
  } = useValidateGcpVpc(
    provisionRequest.clusterName,
    provisionRequest.gcpProjectId,
    gcpSelectedVpc?.id
  );

  const gcpVpcHasError =
    !gcpVpcValidating &&
    (gcpVpcValidateError || (vpcValidation != null && !vpcValidation.valid));

  const gcpVpcValidationMsg = useMemo(() => {
    if (gcpVpcValidateError) return "Failed to validate VPC. Please try again.";
    if (gcpVpcValidating || !vpcValidation) return "";
    if (vpcValidation.valid) return "VPC is valid and ready for provisioning.";
    return vpcValidation.reason || "VPC is not suitable for provisioning.";
  }, [vpcValidation, gcpVpcValidating, gcpVpcValidateError]);

  const gcpVpcHelperText = useMemo(() => {
    if (gcpVpcsFetching) return "Loading VPCs from your project...";
    if (gcpVpcValidating) return "Validating VPC...";
    return (
      gcpVpcValidationMsg ||
      "Select an empty VPC from your GCP project. Only custom-mode VPCs with no existing subnets are supported."
    );
  }, [gcpVpcsFetching, gcpVpcValidating, gcpVpcValidationMsg]);

  const { data: fixedDomains } = useFixedDomains();

  const gcpBaseDomainOptions = useMemo(() => {
    if (fixedDomains && fixedDomains.length > 0) {
      return fixedDomains.map((domain: string) => ({
        id: domain,
        title: `${domain} - Orkes`,
        fixedDomain: true,
      }));
    }
    return [];
  }, [fixedDomains]);

  useEffect(() => {
    if (gcpBaseDomainOptions.length > 0 && !gcpBaseDomain) {
      setGcpBaseDomain(gcpBaseDomainOptions[0]);
    }
  }, [gcpBaseDomainOptions, gcpBaseDomain]);

  // Generate availability zones from the selected GCP region
  const gcpAvailabilityZones = useMemo(() => {
    if (provisionRequest.region && provisionRequest.cloudProvider === "GCP") {
      return GCP_ZONE_SUFFIXES.map((suffix) => ({
        id: `${provisionRequest.region}-${suffix}`,
        title: `${provisionRequest.region}-${suffix}`,
      }));
    }
    return [];
  }, [provisionRequest.region, provisionRequest.cloudProvider]);

  // Auto-populate AZs when SKU mode is active and either the SKU or region changes
  useEffect(() => {
    if (
      gcpSizingMode === "sku" &&
      gcpSelectedSku &&
      gcpAvailabilityZones.length > 0
    ) {
      setGcpSelectedAzs(gcpAvailabilityZones.slice(0, gcpSelectedSku.azCount));
    }
  }, [gcpAvailabilityZones, gcpSizingMode, gcpSelectedSku]);

  const checkClusterNameAction = useActionWithPath({
    onSuccess: async (data: boolean) => {
      if (data) {
        setErrors({
          ...errors,
          ...{ clusterName: "Error: Cluster with that name already exists" },
        });
      }
    },
  });

  const handleCloudProviderChange = (cloudProvider: string) => {
    setProvisionRequest({
      ...provisionRequest,
      cloudProvider,
    });
  };

  const handleChangeData = (
    e: any,
    value?: any,
    reason?: string,
    id?: string
  ) => {
    if (id) {
      setProvisionRequest({
        ...provisionRequest,
        [id]: value,
      });
      // Reset GCP AZ selection when region changes
      if (id === "region" && provisionRequest.cloudProvider === "GCP") {
        setGcpSelectedAzs([]);
      }
    } else {
      if (e.target.name === "customerAccountId") {
        if (e.target.value !== "") {
          let newErrors = { ...errors };
          delete newErrors.customerAccountId;
          setErrors(newErrors);
        } else {
          setErrors({
            ...errors,
            ...{
              customerAccountId: "Cloud provider account id cannot be null",
            },
          });
        }
      } else {
        let newErrors = { ...errors };
        delete newErrors.clusterName;
        delete newErrors.azureResourceGroup;
        delete newErrors.gcpProjectId;
        delete newErrors.gcpVpcCidr;
        delete newErrors.gcpPodsCidr;
        delete newErrors.gcpPscPrefixLength;

        // Real-time CIDR validation for GCP VPC
        if (e.target.name === "gcpVpcCidr" && e.target.value) {
          const cidrError = validateGcpVpcCidr(e.target.value);
          if (cidrError) {
            newErrors["gcpVpcCidr"] = cidrError;
          }
        }

        // Real-time validation for GCP Pods CIDR
        if (e.target.name === "gcpPodsCidr" && e.target.value) {
          const podsCidrError = validateGcpPodsCidr(e.target.value);
          if (podsCidrError) {
            newErrors["gcpPodsCidr"] = podsCidrError;
          }
        }

        // Real-time validation for PSC prefix length
        if (e.target.name === "gcpPscPrefixLength" && e.target.value) {
          const pscError = validateGcpPscPrefixLength(
            parseInt(e.target.value, 10)
          );
          if (pscError) {
            newErrors["gcpPscPrefixLength"] = pscError;
          }
        }

        setErrors(newErrors);
        // Worker hosting renders no cluster-name field, so every text edit here belongs to
        // some other field (resource group, subscription id, …) and the uniqueness check
        // would run against an unrelated value. The name is derived from the parent
        // cluster instead, so there is nothing to check.
        if (!isAzureWorkerHosting) {
          if (clusterNameCheckTimeout) {
            clearTimeout(clusterNameCheckTimeout);
          }
          setClusterNameCheckTimeout(
            setTimeout(() => {
              // @ts-ignore
              checkClusterNameAction.mutate({
                method: "get",
                path: `/clusterNameExists?clusterName=${e.target.value}`,
              });
            }, 500)
          );
        }
      }
      // Parse numeric fields before storing
      const value =
        e.target.name === "gcpPscPrefixLength" && e.target.value !== ""
          ? parseInt(e.target.value, 10)
          : e.target.value;
      setProvisionRequest({
        ...provisionRequest,
        [e.target.name]: value,
      });
    }
  };

  const createCftAction = useActionWithPath({
    onSuccess: (data: CustomerCftDto) => {
      setCftData(data);
      console.log("CFT generation successful");
    },
    onError: async (response: any) => {
      console.error(response);
    },
  });

  const createAzureScriptAction = useActionWithPath({
    onSuccess: (data: AzureScriptDto) => {
      setAzureScriptData(data);
      console.log("Azure script generation successful");
    },
    onError: async (response: any) => {
      console.error(response);
    },
  });

  const createGcpScriptAction = useActionWithPath({
    onSuccess: (data: GcpScriptDto) => {
      setGcpScriptData(data);
      console.log("GCP script generation successful");
    },
    onError: async (response: any) => {
      console.error(response);
      setErrorToast(
        response?.response?.data?.message ?? "Unable to generate GCP script"
      );
    },
  });

  const verifyAuthAction = useActionWithPath({
    onSuccess: () => {
      console.log("Verify auth is successful");
      navigate(
        `/home/newcustomcluster-step2?accountId=${
          provisionRequest.customerAccountId
        }&clusterName=${provisionRequest.clusterName?.toLowerCase()}&cloudProvider=${
          provisionRequest.cloudProvider
        }`
      );
    },
  });

  const verifyAuthActionAzure = useActionWithPath({
    onSuccess: () => {
      setProvisionRequest({
        ...provisionRequest,
        azureCredentialsValidated: true,
      });
    },
  });

  const verifyAuthActionGcp = useActionWithPath({
    onSuccess: () => {
      setProvisionRequest({
        ...provisionRequest,
        gcpCredentialsValidated: true,
      });
      closeDialog();
    },
    onError: async (response: any) => {
      console.error(response);
      setErrorToast(
        response?.response?.data?.message ?? "Unable to verify GCP credentials"
      );
    },
  });

  function createCft() {
    if (validateDataForCft(provisionRequest, errors, setErrors)) {
      // @ts-ignore
      createCftAction.mutate({
        method: "get",
        // byon selects the template that grants no networking permissions. Omitting it defaults
        // the server to false and hands the customer the full role, which can create, modify and
        // delete subnets, route tables and gateways in a VPC they were told Orkes would not touch.
        path: `/generateOrkesRoleCft?clusterName=${provisionRequest.clusterName?.toLowerCase()}&byon=${byon}`,
      });
    }
  }

  function createAzureScript() {
    if (
      validateDataForAzure(
        provisionRequest,
        errors,
        setErrors,
        isAzureWorkerHosting,
        azureSizingMode,
        activeAzureSku
      )
    ) {
      // @ts-ignore
      createAzureScriptAction.mutate({
        method: "get",
        // workerHosting selects the reduced role: azurescript-worker-hosting.ftl with
        // role-template-worker-hosting.bicep. Omitting it defaults the server to false and hands
        // the customer the full enterprise role, which grants Redis, Postgres, storage accounts,
        // Key Vault, private endpoints, private DNS, public IPs and monitoring that a worker
        // environment never creates, and asks for a Microsoft Graph permission needing tenant
        // admin consent that the worker hosting script deliberately does not (CLOUD-1193).
        path: `/generateOrkesAzureRoleScript?resourceGroupName=${provisionRequest.azureResourceGroup?.toLowerCase()}&clusterName=${provisionRequest.clusterName?.toLowerCase()}&workerHosting=${isAzureWorkerHosting}`,
      });
    }
  }

  const openAuthDialog = () => {
    if (validateDataForCft(provisionRequest, errors, setErrors)) {
      setAuthDialogOpen(true);
      createCft();
    }
  };

  const openAzureAuthDialog = () => {
    // Worker hosting has no clusterName field, so without the flag this validated the
    // non-existent clusterName and set the error on an unrendered field — the button went
    // disabled with nothing visible on "Attach To Cluster" (CLOUD-1193).
    if (
      validateDataForAzure(
        provisionRequest,
        errors,
        setErrors,
        isAzureWorkerHosting,
        azureSizingMode,
        activeAzureSku
      )
    ) {
      createAzureScript();
      setAuthDialogOpen(true);
    }
  };

  function createGcpScript() {
    if (
      validateDataForGcp(
        provisionRequest,
        errors,
        setErrors,
        gcpUseSharedVpc,
        gcpSizingMode,
        gcpSelectedSku
      )
    ) {
      // @ts-ignore
      createGcpScriptAction.mutate({
        method: "get",
        path: `/generateOrkesGcpRoleScript?clusterName=${provisionRequest.clusterName?.toLowerCase()}&projectId=${
          provisionRequest.gcpProjectId
        }&region=${provisionRequest.region}`,
      });
    }
  }

  const openGcpAuthDialog = () => {
    if (
      validateDataForGcp(
        provisionRequest,
        errors,
        setErrors,
        gcpUseSharedVpc,
        gcpSizingMode,
        gcpSelectedSku
      )
    ) {
      createGcpScript();
      setAuthDialogOpen(true);
    }
  };

  // Worker hosting attaches to an EXISTING cluster, so the parent is picked from the org's
  // clusters rather than typed in — we are not creating a cluster here. Worker environments
  // themselves are excluded: they cannot be a parent.
  const { data: existingClusters } = useClusterListing();
  const parentClusterItems: IdTitle[] = useMemo(
    () =>
      (existingClusters || [])
        // The rules live in isEligibleParentCluster so they are stated once and testable; see there
        // for why cloud provider and Orkes-hosted are deliberately not among them.
        .filter(isEligibleParentCluster)
        .map(({ id, name }) => ({ id, title: name })),
    [existingClusters]
  );

  const [azureParentCluster, setAzureParentCluster] = useState<any>(null);

  function triggerProvisioningFlow() {
    const clusNameLc = provisionRequest.clusterName?.toLocaleLowerCase();
    let ingressType = provisionRequest.ingressType
      ? provisionRequest.ingressType
      : "EXTERNAL";

    // Set correct customerAccountId based on cloud provider
    let customerAccountId = provisionRequest.customerAccountId;
    if (provisionRequest.cloudProvider === "AZURE") {
      customerAccountId = "azure";
      // Worker hosting exposes no public ingress: only an internal load balancer
      if (isAzureWorkerHosting) {
        ingressType = "INTERNAL";
      }
    } else if (provisionRequest.cloudProvider === "GCP") {
      customerAccountId = provisionRequest.gcpProjectId;
      // Use GCP-specific ingress type selection
      ingressType = gcpIngressType?.id || "EXTERNAL";
    }

    // Set GCP defaults from OCU selection
    let gcpDefaults = {};
    if (provisionRequest.cloudProvider === "GCP") {
      const vpcCidr = provisionRequest.gcpVpcCidr || "10.0.0.0/16";
      // Default AZs: all 3 zones if none selected
      const selectedAzIds =
        gcpSelectedAzs.length > 0
          ? gcpSelectedAzs.map((az: any) => az.id || az)
          : GCP_ZONE_SUFFIXES.map(
              (suffix) => `${provisionRequest.region}-${suffix}`
            );
      gcpDefaults = {
        gcpProjectId: provisionRequest.gcpProjectId,
        gcpVpcCidr: vpcCidr,
        gcpSubnetCidr: (() => {
          const match = vpcCidr.match(/\/(\d+)$/);
          const vpcMask = match ? parseInt(match[1], 10) : 16;
          const subnetMask = Math.min(vpcMask + 1, 28);
          return vpcCidr.replace(/\/\d+$/, `/${subnetMask}`);
        })(),
        gcpNodePoolMachineType: gcpOcu.machineType,
        gcpNodePoolSize: gcpOcu.minNodes,
        gcpNodePoolMinSize: gcpOcu.minNodes,
        gcpNodePoolMaxSize: gcpOcu.maxNodes,
        gcpEnableAutoScaling: true,
        azIds: selectedAzIds,
        // VPC ownership flags
        gcpSharedVpc: gcpUseSharedVpc,
        gcpCreateVpc: gcpUseExistingVpc
          ? false
          : gcpUseSharedVpc
          ? false
          : true,
        gcpVpcName: gcpUseExistingVpc ? gcpSelectedVpc?.id : undefined,
      };
    }

    // parentClusterId is already on provisionRequest, set alongside the derived clusterName
    // when the parent was selected
    const azureWorkerHostingDefaults = isAzureWorkerHosting
      ? { workerHosting: true }
      : {};

    const newProvReq = {
      ...provisionRequest,
      ...gcpDefaults,
      ...azureWorkerHostingDefaults,
      clusterName: clusNameLc,
      ingressType: ingressType,
      customerAccountId: customerAccountId,
      environmentType: environmentType.id,
      // Sizing is resolved either by skuName or by computeUnits, never both: the backend rejects
      // a request carrying both, and computeUnits left set would silently win on the legacy path.
      ...(activeAzureSku
        ? { skuName: activeAzureSku.skuName, computeUnits: undefined }
        : activeGcpSku
        ? { skuName: activeGcpSku.skuName, computeUnits: undefined }
        : {
            skuName: undefined,
            computeUnits:
              provisionRequest.cloudProvider === "GCP"
                ? parseInt(gcpOcu.id)
                : 1,
          }),
      domainUrl:
        provisionRequest.cloudProvider === "GCP" &&
        gcpSubDomain &&
        gcpBaseDomain
          ? `${gcpSubDomain}.${gcpBaseDomain.id}`
          : gcpBaseDomain
          ? `generate.${gcpBaseDomain.id}`
          : undefined,
      enableApiGateway: enableApiGateway,
    };
    // @ts-ignore
    provisionEnvironmentAction.mutate({
      method: "post",
      path: `/provisionEnvironment`,
      body: JSON.stringify(newProvReq),
      successMessage: `${
        isAzureWorkerHosting
          ? `Worker hosting ${clusNameLc} for ${azureParentCluster?.title}`
          : `Cluster ${clusNameLc}`
      } has been scheduled for provisioning. Redirecting you to monitor provisioning.`,
    });
  }

  const provisionEnvironmentAction = useActionWithPath({
    onSuccess: (data: CustomerEnvironment) => {
      setTimeout(() => {
        navigate(
          `/home/clusterprogess?clusterName=${data.name}&showSucess=true`
        );
      }, 3000);
    },
  });

  const closeDialogAndVerify = (verify: boolean) => {
    if (authDialogOpen) {
      setAuthDialogOpen(false);
    }
    if (verify) {
      if (validateDataForCft(provisionRequest, errors, setErrors)) {
        // @ts-ignore
        verifyAuthAction.mutate({
          method: "get",
          path: `/getRegions?clusterName=${provisionRequest.clusterName?.toLowerCase()}&customerAccountId=${
            provisionRequest.customerAccountId
          }`,
          errorMessage:
            "Error verifying authorization. Please check that the CFT stack has been created successfully and try again.",
        });
      }
    }
  };

  const closeDialogAndVerifyAzure = (verify: boolean) => {
    if (authDialogOpen) {
      setAuthDialogOpen(false);
    }
    if (verify) {
      if (
        validateDataForAzure(
          provisionRequest,
          errors,
          setErrors,
          isAzureWorkerHosting,
          azureSizingMode,
          activeAzureSku
        )
      ) {
        // @ts-ignore
        verifyAuthActionAzure.mutate({
          method: "post",
          path: `/verifyAzureConnectivity`,
          body: JSON.stringify(provisionRequest),
          // Prefixed to the server's message, not a replacement, so it stays neutral.
          errorMessage: "Could not verify your Azure setup.",
        });
      }
    }
  };

  const closeDialogAndVerifyGcp = (verify: boolean) => {
    if (!verify) {
      if (authDialogOpen) {
        setAuthDialogOpen(false);
      }
    } else {
      if (
        validateDataForGcp(
          provisionRequest,
          errors,
          setErrors,
          gcpUseSharedVpc,
          gcpSizingMode,
          gcpSelectedSku
        )
      ) {
        // @ts-ignore
        verifyAuthActionGcp.mutate({
          method: "post",
          path: `/verifyGcpConnectivity`,
          body: JSON.stringify(provisionRequest),
          errorMessage:
            "Error verifying authorization. Please check that the script has run successfully and credentials are correct and try again.",
        });
      }
    }
  };

  const closeDialog = () => {
    if (authDialogOpen) {
      setAuthDialogOpen(false);
    }
  };

  function getAWSSection() {
    return (
      <>
        <Grid container spacing={2} sx={{ width: 600 }}>
          <ClusterField
            id={"clusterName"}
            inputProps={{ maxLength: MAX_CLUSTER_NAME_SIZE }}
            onChange={(e) => handleChangeData(e)}
            label={"Cluster Name"}
            type={"text"}
            error={errors["clusterName"] !== undefined}
            ddWidth={300}
            helperText={
              errors?.clusterName
                ? errors.clusterName
                : `Enter a name for your cluster (Max ${MAX_CLUSTER_NAME_SIZE} chars, only alphabets and hyphens are allowed).`
            }
          />
          <ClusterField
            id={"customerAccountId"}
            onChange={(e) => handleChangeData(e)}
            error={errors["customerAccountId"] !== undefined}
            label={"AWS Account Id"}
            type={"text"}
            ddWidth={300}
            helperText={
              "Enter the account id of your AWS cloud account. You can find this in your cloud console."
            }
          />
        </Grid>

        <p className={"headerValue"} style={{ paddingTop: 20, fontSize: 14 }}>
          Before continuing to the next step, we need to authorize access to
          your cloud account. Authorizing access will allow Orkes to create and
          provision the required assets in your cloud account.
        </p>

        {provisionRequest.cloudProvider === "AWS" && (
          <Grid container spacing={2} sx={{ width: 700 }}>
            <Grid item xs={4}>
              <div className={"headerText"}>Customer-owned networking</div>
            </Grid>
            <Grid item xs={8}>
              <Checkbox
                id={"byon"}
                checked={byon}
                // Locked once the template has been generated. The two variants grant different
                // permissions under the same role name and external id, so the server refuses to
                // swap them for a cluster name that already has one -- leaving the box editable
                // only lets someone walk into that dead end.
                disabled={!!cftData.consistentId}
                onChange={(e, checked) => setByon(checked)}
              />
              <div className={"headerValue"} style={{ fontSize: 13 }}>
                You create the VPC, subnets, route tables and gateways yourself,
                and Orkes creates none of them: the CloudFormation template
                generated for you grants no permission to create, modify or
                delete them.
                <br />
                <br />
                Choose this for any VPC you build yourself. It is required if
                your VPC has no internet gateway &mdash; for example, one that
                reaches the internet through a Transit Gateway.
                <br />
                <br />
                Your VPC must be dedicated to this one cluster, and must have
                DNS resolution and DNS hostnames enabled, as Orkes cannot enable
                them for you. Leave this unticked if you would like Orkes to
                create the networking.
                {cftData.consistentId
                  ? " This choice is now fixed for this cluster name; to change it, start again with a new name."
                  : " This cannot be changed once you authorize Orkes below."}
              </div>
            </Grid>
          </Grid>
        )}

        <StyledMainButton
          disabled={Object.keys(errors).length !== 0}
          size="small"
          color={"inherit"}
          style={{
            width: 250,
            fontSize: 14,
            marginRight: 30,
            marginTop: 15,
          }}
          onClick={() => {
            openAuthDialog();
          }}
        >
          Authorize Orkes
        </StyledMainButton>

        <p className={"headerValue"} style={{ paddingTop: 30, fontSize: 14 }}>
          If you have already created a stack, you can continue with the
          verification.
        </p>
        <StyledMainButton
          size="small"
          color={"inherit"}
          style={{
            width: 250,
            fontSize: 14,
            marginRight: 10,
            marginTop: 10,
          }}
          onClick={() => {
            closeDialogAndVerify(true);
          }}
        >
          Verify Authorization & Continue
        </StyledMainButton>
      </>
    );
  }

  function getAzureSection() {
    return (
      <>
        <Grid container spacing={2} sx={{ width: 600 }}>
          {/* Worker hosting (CLOUD-1193) is a distinct product: the Conductor system
              workers run in the customer's subscription and attach to an existing
              Orkes-hosted cluster, so no new cluster is created here */}
          {provisionRequest.azureCredentialsValidated !== true && (
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Grid
                item
                xs={4}
                sx={{
                  fontSize: "14px",
                  fontFamily: "Lexend, sans-serif",
                  fontWeight: 600,
                  pb: 1,
                }}
              >
                Deployment Type
              </Grid>
              <RadioGroup
                name="azureDeploymentKind"
                value={azureDeploymentKind}
                onChange={(_, value) => {
                  setAzureDeploymentKind(value as "cluster" | "worker");
                  setErrors({});
                }}
              >
                <FormControlLabel
                  value="cluster"
                  control={<Radio size="small" />}
                  label="Conductor cluster"
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: 14 } }}
                />
                <FormControlLabel
                  value="worker"
                  control={<Radio size="small" />}
                  label="Worker hosting (attach workers to an existing cluster)"
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: 14 } }}
                />
              </RadioGroup>
            </Grid>
          )}
          {isAzureWorkerHosting ? (
            <>
              <ClusterField
                id={"parentClusterId"}
                onChange={(e, value) => {
                  setAzureParentCluster(value);
                  setProvisionRequest({
                    ...provisionRequest,
                    parentClusterId: value?.id,
                    clusterName: workerEnvironmentName(value?.title),
                  });
                }}
                error={errors["parentClusterId"] !== undefined}
                ddWidth={300}
                items={parentClusterItems}
                value={azureParentCluster}
                label={"Attach To Cluster"}
                labelValue={azureParentCluster?.title}
                type={
                  provisionRequest.azureCredentialsValidated
                    ? "label"
                    : "select"
                }
                helperText={
                  errors?.parentClusterId
                    ? errors.parentClusterId
                    : "Select the Orkes-hosted Conductor cluster these workers will attach to."
                }
              />
              {/* Seeded from the parent, then editable. It was read-only, which meant exactly
                  one worker environment per parent for all time: the provision endpoint rejects
                  a duplicate name and clusterExists does not filter deleted rows, so a torn-down
                  environment kept its name forever. Changing the parent re-seeds this, so an edit
                  is only preserved while the parent stays put. */}
              <ClusterField
                id={"clusterName"}
                inputProps={{ maxLength: MAX_CLUSTER_NAME_SIZE }}
                onChange={(e) => handleChangeData(e)}
                label={"Worker Environment Name"}
                labelValue={provisionRequest.clusterName}
                // ClusterField only renders labelValue under type="label"; on the "text"
                // branch it is destructured away and never reaches the Input. Seeding the
                // name on parent selection therefore left the box empty, and the derived
                // name only appeared at provision time -- the surprise this field exists to
                // avoid. value keeps it controlled so the seed shows and re-seeds when the
                // parent changes, while handleChangeData keeps it editable.
                value={provisionRequest.clusterName ?? ""}
                type={
                  provisionRequest.azureCredentialsValidated ? "label" : "text"
                }
                error={errors["clusterName"] !== undefined}
                ddWidth={300}
                helperText={
                  errors?.clusterName
                    ? errors.clusterName
                    : `Defaults to the parent's name plus "${WORKER_ENVIRONMENT_NAME_SUFFIX}". Must be unique (max ${MAX_CLUSTER_NAME_SIZE} chars, letters and hyphens).`
                }
              />
            </>
          ) : (
            <ClusterField
              id={"clusterName"}
              inputProps={{ maxLength: MAX_CLUSTER_NAME_SIZE }}
              onChange={(e) => handleChangeData(e)}
              label={"Cluster Name"}
              labelValue={provisionRequest.clusterName}
              type={
                provisionRequest.azureCredentialsValidated ? "label" : "text"
              }
              error={errors["clusterName"] !== undefined}
              ddWidth={300}
              helperText={
                errors?.clusterName
                  ? errors.clusterName
                  : `Enter a name for your cluster (Max ${MAX_CLUSTER_NAME_SIZE} chars, only alphabets and hyphens are allowed).`
              }
            />
          )}
          <ClusterField
            id={"azureResourceGroup"}
            onChange={(e) => handleChangeData(e)}
            error={errors["azureResourceGroup"] !== undefined}
            label={"Azure Resource Group Name"}
            labelValue={provisionRequest.azureResourceGroup}
            type={provisionRequest.azureCredentialsValidated ? "label" : "text"}
            ddWidth={300}
            helperText={"Enter the name of your azure resource group."}
          />
          <ClusterField
            id={"region"}
            onChange={(e, value, reason) =>
              handleChangeData(
                e,
                getValueForAutocomplete(value),
                reason,
                "region"
              )
            }
            error={errors["region"] !== undefined}
            ddWidth={300}
            items={azureSupportedRegions ? azureSupportedRegions : []}
            label={"Azure Region"}
            labelValue={provisionRequest.region}
            type={
              provisionRequest.azureCredentialsValidated ? "label" : "select"
            }
            helperText={
              "Enter the region where you want to provision this cluster."
            }
          />
        </Grid>

        {provisionRequest.azureCredentialsValidated !== true && (
          <>
            <p
              className={"headerValue"}
              style={{ paddingTop: 20, fontSize: 14 }}
            >
              Before continuing to the next step, we need to authorize access to
              your cloud account. Authorizing access will allow Orkes to create
              and provision the required assets in your cloud account.
            </p>

            <p
              className={"headerValue"}
              style={{ paddingTop: 20, fontSize: 14 }}
            >
              To authorize - we will generate a script that the administrator of
              your cloud account needs to run. This requires AZ CLI. In this
              script, we will create the applications, service principals, roles
              and role assignments required for cluster provisioning. The script
              will print credentials for the service principal, which is
              required in this request form to continue cluster provisioning.
            </p>

            <StyledMainButton
              disabled={
                Object.keys(errors).length !== 0 ||
                verifyAuthActionAzure.isLoading ||
                createAzureScriptAction.isLoading
              }
              size="small"
              color={"inherit"}
              style={{
                width: 250,
                fontSize: 14,
                marginRight: 30,
                marginTop: 15,
              }}
              onClick={() => {
                openAzureAuthDialog();
              }}
            >
              Generate Script
            </StyledMainButton>

            <p
              className={"headerValue"}
              style={{ paddingTop: 30, fontSize: 14 }}
            >
              If you have already run the script and have gathered the
              credentials, you can continue with the verification.
            </p>

            <ClusterField
              id={"azureSubscriptionId"}
              onChange={(e) => handleChangeData(e)}
              error={errors["azureSubscriptionId"] !== undefined}
              label={"Azure Subscription Id"}
              type={"text"}
              ddWidth={400}
              helperText={
                "Enter the subscription id where the resource group exists."
              }
            />
            <ClusterField
              id={"azureServicePrincipalApplicationId"}
              onChange={(e) => handleChangeData(e)}
              error={errors["azureApplicationId"] !== undefined}
              label={"Azure Service Principal Application Id"}
              type={"text"}
              ddWidth={400}
              helperText={"Enter the application id for the service principal."}
            />
            <ClusterField
              id={"azureServicePrincipalPassword"}
              onChange={(e) => handleChangeData(e)}
              error={errors["azureTenantId"] !== undefined}
              label={"Azure Service Principal Password"}
              type={"password"}
              ddWidth={400}
              helperText={"Enter the tenant id for the service principal."}
            />
            <ClusterField
              id={"azureTenantId"}
              onChange={(e) => handleChangeData(e)}
              error={errors["azureTenantId"] !== undefined}
              label={"Azure Tenant Id"}
              type={"text"}
              ddWidth={400}
              helperText={"Enter the tenant id for the service principal."}
            />

            <StyledMainButton
              size="small"
              color={"inherit"}
              disabled={verifyAuthActionAzure.isLoading}
              style={{
                width: 250,
                fontSize: 14,
                marginRight: 10,
                marginTop: 10,
              }}
              onClick={() => {
                closeDialogAndVerifyAzure(true);
              }}
            >
              {verifyAuthActionAzure.isLoading && (
                <>
                  <CircularProgress
                    size={24}
                    sx={{
                      color: green[500],
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                  <span>Verifying...</span>
                </>
              )}
              {!verifyAuthActionAzure.isLoading &&
                "Verify Authorization & Continue"}
            </StyledMainButton>
          </>
        )}

        {provisionRequest.azureCredentialsValidated === true && (
          <>
            {isAzureWorkerHosting && (
              <ClusterField
                id={"azureExistingSubnetId"}
                onChange={(e) => handleChangeData(e)}
                error={errors["azureExistingSubnetId"] !== undefined}
                label={"Existing Subnet ID (optional)"}
                type={"text"}
                ddWidth={400}
                helperText={
                  "Optional: full resource ID of an existing subnet to deploy the workers into. When supplied, Orkes does not create a VNet and the VNet fields below are not needed."
                }
              />
            )}
            {/* Bring-your-own subnet replaces the VNet entirely, so the CIDR fields are not
                required (and not shown) when one is supplied */}
            {!provisionRequest.azureExistingSubnetId && (
              <>
                <ClusterField
                  id={"azureVnetName"}
                  onChange={(e) => handleChangeData(e)}
                  error={errors["azureVnetName"] !== undefined}
                  label={"VNet Name?"}
                  type={"text"}
                  ddWidth={400}
                  helperText={"Enter VNet name"}
                />
                <ClusterField
                  id={"azureVnetCidr"}
                  onChange={(e) => handleChangeData(e)}
                  error={errors["azureVnetCidr"] !== undefined}
                  label={"VNet CIDR?"}
                  type={"text"}
                  ddWidth={400}
                  helperText={
                    isAzureWorkerHosting
                      ? "Enter a block-aligned CIDR from /25 to /28, e.g. 10.0.1.0/26. A /16 to /24 ending in .0 is also accepted, but reserves far more address space than the workers use."
                      : azureSizingMode === "sku"
                      ? "Enter VNet CIDR ending in .0 with a /16 to /20 mask. A SKU cluster is carved into two /21 subnets, so it needs /20 or larger."
                      : "Enter VNet CIDR ending in .0 with a /16 to /24 mask (e.g. 10.0.0.0/20)."
                  }
                />
              </>
            )}
            {azureSkuModeAvailable && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  marginTop: 8,
                }}
              >
                <label
                  style={{ display: "flex", alignItems: "center", gap: 6 }}
                >
                  <input
                    type="radio"
                    name="azureSizingMode"
                    checked={azureSizingMode === "legacy"}
                    onChange={() => {
                      setAzureSizingMode("legacy");
                      setErrors((prev: any) => ({
                        ...prev,
                        skuName: undefined,
                        azureVnetCidr: undefined,
                      }));
                    }}
                  />
                  <span>Cluster Capacity (legacy)</span>
                </label>
                <label
                  style={{ display: "flex", alignItems: "center", gap: 6 }}
                >
                  <input
                    type="radio"
                    name="azureSizingMode"
                    checked={azureSizingMode === "sku"}
                    onChange={() => setAzureSizingMode("sku")}
                  />
                  <span>OCU SKU</span>
                </label>
              </div>
            )}
            {azureSkuModeAvailable && azureSizingMode === "sku" && (
              <ClusterField
                id={"skuName"}
                onChange={(e, value) => {
                  setAzureSelectedSku((value as AzureSkuOption) ?? null);
                  setErrors((prev: any) => ({ ...prev, skuName: undefined }));
                }}
                error={errors["skuName"] !== undefined}
                ddWidth={400}
                items={azureSkuItems}
                label={"Cluster SKU"}
                type={"select"}
                multiple={false}
                helperText={
                  "Pick an OCU SKU. Infrastructure sizing and limits come from the SKU. Needs a VNet of /20 or larger."
                }
              />
            )}
            <ClusterField
              id={"environmentType"}
              onChange={(
                e: ChangeEvent<HTMLInputElement>,
                value: EnvironmentTypeOption
              ) => {
                setEnvironmentType(value);
              }}
              error={errors["environmentType"] !== undefined}
              ddWidth={400}
              items={environmentTypeOptions}
              value={environmentType}
              label={"Environment Type"}
              type={"select"}
              helperText={
                "Tag your cluster's environment type. It is helpful for distinguishing between multiple instances."
              }
              showDropdownIcons={true}
              iconHandler={getEnvironmentIcon}
            />

            {!isAzureWorkerHosting && (
              <Grid item xs={12} sx={{ mt: 1 }}>
                <label
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <input
                    type="checkbox"
                    checked={enableApiGateway}
                    onChange={(e) => setEnableApiGateway(e.target.checked)}
                  />
                  <span>Enable API Gateway</span>
                </label>
              </Grid>
            )}

            <p
              className={"headerValue"}
              style={{ paddingTop: 20, fontSize: 14 }}
            >
              Once you have provided / selected all the required info, you can
              click the button below to start the cluster provisioning. Most
              provisioning flows will complete within an hour and we can monitor
              progress.
            </p>

            <StyledMainButton
              size="small"
              color={"inherit"}
              disabled={provisionEnvironmentAction.isLoading}
              style={{
                width: 250,
                fontSize: 15,
                marginRight: 10,
                marginTop: 10,
              }}
              onClick={() => {
                triggerProvisioningFlow();
              }}
            >
              {provisionEnvironmentAction.isLoading && (
                <>
                  <CircularProgress
                    size={24}
                    sx={{
                      color: green[500],
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                  <span>Creating request...</span>
                </>
              )}
              {!provisionEnvironmentAction.isLoading &&
                "Create Provisioning Workflow"}
            </StyledMainButton>
          </>
        )}
      </>
    );
  }

  function getGcpSection() {
    return (
      <>
        <Grid container spacing={2} sx={{ width: 600 }}>
          <ClusterField
            id={"clusterName"}
            inputProps={{ maxLength: MAX_CLUSTER_NAME_SIZE }}
            onChange={(e) => handleChangeData(e)}
            label={"Cluster Name"}
            labelValue={provisionRequest.clusterName}
            type={provisionRequest.gcpCredentialsValidated ? "label" : "text"}
            error={errors["clusterName"] !== undefined}
            ddWidth={300}
            helperText={
              errors?.clusterName
                ? errors.clusterName
                : `Enter a name for your cluster (Max ${MAX_CLUSTER_NAME_SIZE} chars, only alphabets and hyphens are allowed).`
            }
          />
          <ClusterField
            id={"gcpProjectId"}
            onChange={(e) => handleChangeData(e)}
            error={errors["gcpProjectId"] !== undefined}
            label={"GCP Project ID"}
            labelValue={provisionRequest.gcpProjectId}
            type={provisionRequest.gcpCredentialsValidated ? "label" : "text"}
            ddWidth={300}
            helperText={"Enter your GCP Project ID (e.g., my-project-123456)."}
          />
          <ClusterField
            id={"region"}
            onChange={(e, value, reason) =>
              handleChangeData(
                e,
                getValueForAutocomplete(value),
                reason,
                "region"
              )
            }
            error={errors["region"] !== undefined}
            ddWidth={300}
            items={gcpSupportedRegions ? gcpSupportedRegions : []}
            label={"GCP Region"}
            labelValue={provisionRequest.region}
            type={provisionRequest.gcpCredentialsValidated ? "label" : "select"}
            helperText={
              "Select the region where you want to provision this cluster."
            }
          />
        </Grid>

        {provisionRequest.gcpCredentialsValidated !== true && (
          <>
            <p
              className={"headerValue"}
              style={{ paddingTop: 20, fontSize: 14 }}
            >
              Before continuing to the next step, we need to authorize access to
              your GCP project. Authorizing access will allow Orkes to create
              and provision the required resources in your GCP project.
            </p>

            <p
              className={"headerValue"}
              style={{ paddingTop: 20, fontSize: 14 }}
            >
              To authorize - we will generate a script that the administrator of
              your GCP project needs to run. This requires gcloud CLI or can be
              run in GCP Cloud Shell. The script will create a service account,
              custom IAM role, and generate a JSON key. You'll need to paste
              this JSON key in the next step to continue cluster provisioning.
            </p>

            <StyledMainButton
              disabled={
                Object.keys(errors).length !== 0 ||
                verifyAuthActionGcp.isLoading ||
                createGcpScriptAction.isLoading
              }
              size="small"
              color={"inherit"}
              style={{
                width: 250,
                fontSize: 14,
                marginRight: 30,
                marginTop: 15,
              }}
              onClick={() => {
                openGcpAuthDialog();
              }}
            >
              Generate GCP Setup Script
            </StyledMainButton>
          </>
        )}

        {provisionRequest.gcpCredentialsValidated === true && (
          <>
            <p
              className={"headerValue"}
              style={{ paddingTop: 20, fontSize: 14, color: green[600] }}
            >
              ✓ GCP credentials verified successfully! Now configure your
              cluster.
            </p>

            <Grid container spacing={2} sx={{ width: 600 }}>
              <Grid container pl={2} pt={1}>
                <Grid
                  item
                  xs={4}
                  sx={{
                    fontSize: "14px",
                    fontFamily: "Lexend, sans-serif",
                    fontWeight: 600,
                    pt: 1.5,
                  }}
                >
                  {"Domain Url"}
                </Grid>
                <Grid item xs={4} pl={0.6}>
                  <ClusterField
                    id={"gcpSubDomain"}
                    textSuffix={`.`}
                    value={gcpSubDomain}
                    onChange={(e: any) => {
                      setGcpSubDomain(e.target.value);
                    }}
                    error={
                      gcpSubDomain.length > 0 &&
                      !SUB_DOMAIN_REGEX.test(gcpSubDomain)
                    }
                    ddWidth={190}
                    label={""}
                    type={"text"}
                    helperText={
                      gcpSubDomain.length > 0 &&
                      !SUB_DOMAIN_REGEX.test(gcpSubDomain)
                        ? "Only lowercase alphanumeric and hyphens allowed"
                        : "Sub domain"
                    }
                    placeholder={"e.g. my-cluster"}
                  />
                </Grid>
                <Grid item xs={4}>
                  <ClusterField
                    id={"gcpBaseDomain"}
                    value={gcpBaseDomain}
                    onChange={(e: any, value: any) => {
                      if (value) setGcpBaseDomain(value);
                    }}
                    error={false}
                    ddWidth={310}
                    items={gcpBaseDomainOptions}
                    label={""}
                    type={"select"}
                    multiple={false}
                    helperText={
                      "Access to Conductor will be configured in this selected domain"
                    }
                  />
                </Grid>
              </Grid>

              {gcpSkuModeAvailable && (
                <Grid item xs={12}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                      marginTop: 8,
                    }}
                  >
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <input
                        type="radio"
                        name="gcpSizingMode"
                        checked={gcpSizingMode === "legacy"}
                        onChange={() => {
                          setGcpSizingMode("legacy");
                          setGcpSelectedAzs([]);
                          setErrors((prev: any) => ({
                            ...prev,
                            skuName: undefined,
                          }));
                        }}
                      />
                      <span>Cluster Capacity (legacy)</span>
                    </label>
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <input
                        type="radio"
                        name="gcpSizingMode"
                        checked={gcpSizingMode === "sku"}
                        onChange={() => setGcpSizingMode("sku")}
                      />
                      <span>OCU SKU</span>
                    </label>
                  </div>
                </Grid>
              )}
              {gcpSkuModeAvailable && gcpSizingMode === "sku" ? (
                <ClusterField
                  id={"skuName"}
                  onChange={(e, value) => {
                    setGcpSelectedSku((value as GcpSkuOption) ?? null);
                    setErrors((prev: any) => ({ ...prev, skuName: undefined }));
                  }}
                  error={errors["skuName"] !== undefined}
                  ddWidth={500}
                  items={gcpSkuItems}
                  value={gcpSelectedSku}
                  label={"Cluster SKU"}
                  type={"select"}
                  multiple={false}
                  helperText={
                    "Pick an OCU SKU. Infrastructure sizing and limits come from the SKU."
                  }
                />
              ) : (
                <ClusterField
                  id={"gcpOcu"}
                  onChange={(e: ChangeEvent<HTMLInputElement>, value: any) => {
                    if (value) setGcpOcu(value);
                  }}
                  error={false}
                  ddWidth={500}
                  items={gcpOcuOptions}
                  value={gcpOcu}
                  label={"Cluster Capacity"}
                  type={"select"}
                  helperText={
                    "Pick a suitable capacity for your cluster. This can be adjusted later."
                  }
                  showDropdownIcons={true}
                  iconHandler={getComputeUnitIcon}
                />
              )}

              <ClusterField
                id={"gcpAzIds"}
                onChange={(e: any, value: any) => {
                  const max =
                    gcpSizingMode === "sku" && gcpSelectedSku
                      ? gcpSelectedSku.azCount
                      : 3;
                  if (Array.isArray(value) && value.length > max) {
                    return;
                  }
                  setGcpSelectedAzs(value || []);
                }}
                error={false}
                ddWidth={500}
                disableCloseOnSelect={true}
                items={gcpAvailabilityZones}
                value={gcpSelectedAzs}
                label={"Preferred Availability Zones"}
                type={"select"}
                multiple={true}
                helperText={
                  gcpSizingMode === "sku" && gcpSelectedSku
                    ? `${gcpSelectedSku.azCount} AZs required for this SKU — you can swap but not exceed this count.`
                    : "Pick your preferred availability zones. Max of 3. GKE regional clusters spread across these zones."
                }
              />

              <ClusterField
                id={"gcpIngressType"}
                onChange={(e: any, value: any) => {
                  setGcpIngressType(value);
                }}
                error={false}
                ddWidth={400}
                items={gcpIngressTypes}
                value={gcpIngressType}
                label={"Ingress Type"}
                type={"select"}
                multiple={false}
                helperText={
                  "Pick access to Conductor app and UI - via Internet or via VPC"
                }
              />

              <Grid item xs={12} sx={{ mt: 1 }}>
                <label
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <input
                    type="checkbox"
                    checked={enableApiGateway}
                    onChange={(e) => setEnableApiGateway(e.target.checked)}
                  />
                  <span>Enable API Gateway</span>
                </label>
              </Grid>

              <ClusterField
                id={"environmentType"}
                onChange={(
                  e: ChangeEvent<HTMLInputElement>,
                  value: EnvironmentTypeOption
                ) => {
                  setEnvironmentType(value);
                }}
                error={errors["environmentType"] !== undefined}
                ddWidth={300}
                items={environmentTypeOptions}
                value={environmentType}
                label={"Environment Type"}
                type={"select"}
                helperText={
                  "Tag your cluster's environment type. It is helpful for distinguishing between multiple instances."
                }
                showDropdownIcons={true}
                iconHandler={getEnvironmentIcon}
              />

              <ClusterField
                id={"enableAuthentication"}
                onChange={(e, value, reason) =>
                  handleChangeData(
                    e,
                    getValueForAutocomplete(value),
                    reason,
                    "enableAuthentication"
                  )
                }
                error={errors["enableAuthentication"] !== undefined}
                ddWidth={300}
                defaultValue={{ id: "true", title: "Yes" }}
                items={[
                  { id: "true", title: "Yes" },
                  { id: "false", title: "No" },
                ]}
                label={"Enable authentication"}
                type={"select"}
                multiple={false}
                helperText={
                  "Set this to Yes to protect access to your Conductor cluster. A default authorization configuration will be added and you can change this to your own OAuth provider."
                }
              />

              <Grid item xs={12} sx={{ mt: 1 }}>
                <Grid
                  item
                  xs={4}
                  sx={{
                    fontSize: "14px",
                    fontFamily: "Lexend, sans-serif",
                    fontWeight: 600,
                    pb: 1,
                  }}
                >
                  VPC Configuration
                </Grid>
                <RadioGroup
                  name="gcpVpcMode"
                  value={gcpVpcMode}
                  onChange={(_, value) => {
                    setGcpVpcMode(value as "new" | "existing" | "shared");
                    setGcpSelectedVpc(null);
                  }}
                >
                  <FormControlLabel
                    value="new"
                    control={<Radio size="small" />}
                    label="Create a new VPC"
                    sx={{ "& .MuiFormControlLabel-label": { fontSize: 14 } }}
                  />
                  <FormControlLabel
                    value="existing"
                    control={<Radio size="small" />}
                    label="Use an existing VPC from your project"
                    sx={{ "& .MuiFormControlLabel-label": { fontSize: 14 } }}
                  />
                  <FormControlLabel
                    value="shared"
                    control={<Radio size="small" />}
                    label="Use Shared VPC (network managed in a separate host project)"
                    sx={{ "& .MuiFormControlLabel-label": { fontSize: 14 } }}
                  />
                </RadioGroup>
              </Grid>

              {gcpUseExistingVpc && (
                <>
                  <ClusterField
                    id={"gcpExistingVpc"}
                    onChange={(e: any, value: any) => {
                      setGcpSelectedVpc(value);
                    }}
                    getOptionDisabled={(option: any) => !option.isEmpty}
                    error={gcpVpcHasError}
                    ddWidth={400}
                    items={gcpVpcItems}
                    value={gcpSelectedVpc}
                    label={"Select VPC"}
                    type={"select"}
                    multiple={false}
                    helperText={gcpVpcHelperText}
                  />
                  {gcpVpcHasError && (
                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ ml: 2 }}
                      >
                        {gcpVpcValidationMsg}
                      </Typography>
                    </Grid>
                  )}
                </>
              )}

              {/* Shared VPC inputs — shown when network team manages VPC in a separate host project */}
              {gcpUseSharedVpc && (
                <>
                  <ClusterField
                    id={"gcpHostProjectId"}
                    value={provisionRequest.gcpHostProjectId ?? ""}
                    onChange={(e) => handleChangeData(e)}
                    label={"Host Project ID"}
                    type={"text"}
                    ddWidth={400}
                    helperText={
                      "GCP project ID where your network team manages the VPC/subnets (e.g. toorak-network-prod)"
                    }
                    placeholder={"toorak-network-prod"}
                  />

                  {provisionRequest.gcpHostProjectId && (
                    <Grid item xs={12} sx={{ mt: 1, mb: 1 }}>
                      <Box
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRadius: 2,
                          p: 2,
                          backgroundColor: "#f9f9f9",
                        }}
                      >
                        <Typography sx={{ fontSize: 13, mb: 1, color: "#444" }}>
                          Share this script with your network team. They run it
                          once in the host project — it sets up the VPC, subnet,
                          PSA, NAT, firewall rules, and all IAM grants. The
                          script prints the exact self-links to paste into the
                          fields below.
                        </Typography>
                        <BootstrapOutlineButton
                          size="small"
                          onClick={downloadSharedVpcScript}
                          style={{ fontSize: 13 }}
                        >
                          ⬇ Download Host Project Setup Script
                        </BootstrapOutlineButton>
                      </Box>
                    </Grid>
                  )}
                  <ClusterField
                    id={"gcpExistingVpcSelfLink"}
                    value={provisionRequest.gcpExistingVpcSelfLink ?? ""}
                    onChange={(e) => handleChangeData(e)}
                    label={"VPC Self-Link"}
                    type={"text"}
                    ddWidth={400}
                    helperText={
                      "Full self-link of the shared VPC (e.g. projects/toorak-network-prod/global/networks/shared-vpc)"
                    }
                    placeholder={
                      "projects/toorak-network-prod/global/networks/shared-vpc"
                    }
                  />
                  <ClusterField
                    id={"gcpExistingSubnetSelfLink"}
                    value={provisionRequest.gcpExistingSubnetSelfLink ?? ""}
                    onChange={(e) => handleChangeData(e)}
                    label={"Subnet Self-Link"}
                    type={"text"}
                    ddWidth={400}
                    helperText={
                      "Full self-link of the subnet Orkes will deploy into (e.g. projects/toorak-network-prod/regions/us-central1/subnetworks/orkes-subnet)"
                    }
                    placeholder={
                      "projects/toorak-network-prod/regions/us-central1/subnetworks/orkes-subnet"
                    }
                  />
                  <ClusterField
                    id={"gcpPodsRangeName"}
                    value={provisionRequest.gcpPodsRangeName ?? ""}
                    onChange={(e) => handleChangeData(e)}
                    label={"Pods Secondary Range Name"}
                    type={"text"}
                    ddWidth={400}
                    helperText={
                      "Name of the secondary IP range on the subnet reserved for GKE pods"
                    }
                    placeholder={"pods"}
                  />
                  <ClusterField
                    id={"gcpServicesRangeName"}
                    value={provisionRequest.gcpServicesRangeName ?? ""}
                    onChange={(e) => handleChangeData(e)}
                    label={"Services Secondary Range Name"}
                    type={"text"}
                    ddWidth={400}
                    helperText={
                      "Name of the secondary IP range on the subnet reserved for GKE services"
                    }
                    placeholder={"services"}
                  />
                </>
              )}

              {/* VPC CIDR - needed in new/existing modes for subnet range calculations; hidden for Shared VPC */}
              {!gcpUseSharedVpc && (
                <ClusterField
                  id={"gcpVpcCidr"}
                  onChange={(e) => handleChangeData(e)}
                  error={errors["gcpVpcCidr"] !== undefined}
                  label={"VPC CIDR Range"}
                  type={"text"}
                  ddWidth={400}
                  helperText={
                    errors["gcpVpcCidr"] ||
                    (gcpUseExistingVpc
                      ? "Enter the CIDR range to use for subnets inside your VPC (/16 to /24 mask, e.g., 10.0.0.0/16)."
                      : "Private IP range with /16 to /24 mask (e.g., 10.0.0.0/16). VPC and subnets will be auto-provisioned.")
                  }
                  placeholder={"10.0.0.0/16"}
                />
              )}

              {/* Pod CIDR — GKE secondary IP range for Kubernetes pods. Hidden for Shared VPC (ranges come from existing subnet). */}
              {!gcpUseSharedVpc && (
                <ClusterField
                  id={"gcpPodsCidr"}
                  onChange={(e) => handleChangeData(e)}
                  error={errors["gcpPodsCidr"] !== undefined}
                  label={"Pod CIDR Range (optional)"}
                  type={"text"}
                  ddWidth={400}
                  helperText={
                    errors["gcpPodsCidr"] ||
                    "IP range for GKE pods. Default: 10.128.0.0/17. Min /24. Immutable after provisioning."
                  }
                  placeholder={"10.128.0.0/17"}
                />
              )}

              {/* PSC Prefix Length — size of the Private Service Connection IP reservation for Cloud SQL and Redis */}
              {!gcpUseSharedVpc && (
                <ClusterField
                  id={"gcpPscPrefixLength"}
                  onChange={(e) => handleChangeData(e)}
                  error={errors["gcpPscPrefixLength"] !== undefined}
                  label={"PSC Prefix Length (optional)"}
                  type={"number"}
                  ddWidth={400}
                  helperText={
                    errors["gcpPscPrefixLength"] ||
                    "Prefix length for the Private Service Connection IP block (Cloud SQL & Redis). Default: 16 (/16). Min: 24 (/24). Immutable after provisioning."
                  }
                  placeholder={"16"}
                />
              )}
            </Grid>

            <p
              className={"headerValue"}
              style={{ paddingTop: 20, fontSize: 14 }}
            >
              Once you have provided / selected all the required info, you can
              click the button below to start the cluster provisioning. Most
              provisioning flows will complete within an hour and we can monitor
              progress.
            </p>

            <StyledMainButton
              size="small"
              color={"inherit"}
              disabled={provisionEnvironmentAction.isLoading}
              style={{
                width: 250,
                fontSize: 15,
                marginRight: 10,
                marginTop: 10,
              }}
              onClick={() => {
                triggerProvisioningFlow();
              }}
            >
              {provisionEnvironmentAction.isLoading && (
                <>
                  <CircularProgress
                    size={24}
                    sx={{
                      color: green[500],
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                  <span>Creating request...</span>
                </>
              )}
              {!provisionEnvironmentAction.isLoading &&
                "Create Provisioning Workflow"}
            </StyledMainButton>
          </>
        )}
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Orkes Cloud - Create New Customer Cluster</title>
      </Helmet>
      {(createCftAction.isLoading ||
        verifyAuthAction.isLoading ||
        verifyAuthActionAzure.isLoading ||
        createGcpScriptAction.isLoading ||
        verifyAuthActionGcp.isLoading) && <LinearProgress />}

      {authDialogOpen && provisionRequest.cloudProvider === "AWS" && (
        <AuthorizeAccountDialog
          cftData={cftData}
          handleClose={() => closeDialogAndVerify(false)}
          verifyMethod={() => closeDialogAndVerify(true)}
          progressPending={createCftAction.isLoading}
        />
      )}
      {authDialogOpen && provisionRequest.cloudProvider === "AZURE" && (
        <AzureAuthorizeAccountDialog
          azureScriptData={azureScriptData}
          handleClose={() => closeDialogAndVerify(false)}
          verifyMethod={() => closeDialogAndVerify(true)}
          progressPending={createAzureScriptAction.isLoading}
        />
      )}
      {authDialogOpen && provisionRequest.cloudProvider === "GCP" && (
        <GcpAuthorizeAccountDialog
          gcpScriptData={gcpScriptData}
          handleClose={() => closeDialogAndVerifyGcp(false)}
          verifyMethod={() => closeDialogAndVerifyGcp(true)}
          progressPending={
            createGcpScriptAction.isLoading || verifyAuthActionGcp.isLoading
          }
          provisionRequest={provisionRequest}
          setProvisionRequest={setProvisionRequest}
        />
      )}
      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: "row",
          borderBottom: "1px solid rgb(200,200,200, 0.5)",
        }}
      >
        <Box
          style={{
            display: "flex",
            justifyContent: "left",
            flexDirection: "column",
            paddingLeft: 25,
            paddingRight: 0,
            paddingTop: 5,
          }}
        >
          <h2 style={{ marginBottom: 15 }}>Create New Customer Cluster</h2>
        </Box>
        <Box
          style={{
            display: "flex",
            justifyContent: "left",
            flexDirection: "row",
            paddingRight: 20,
            paddingTop: 5,
          }}
        >
          <BootstrapOutlineButton
            color={"inherit"}
            style={{
              width: 100,
              fontSize: 15,
              lineHeight: "27px",
              marginRight: 10,
              fontFamily: "Lexend, sans-serif",
              fontWeight: 800,
              textAlign: "center",
              borderRadius: 5,
            }}
            //@ts-ignore
            component={Link}
            to={"/home/clusters"}
          >
            Cancel
          </BootstrapOutlineButton>
        </Box>
      </Box>

      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignContent: "center",
          flexDirection: "column",
          width: "100%",
          // borderRight: "2px solid lightgrey",
        }}
      >
        <Paper
          style={{
            marginTop: 30,
            marginLeft: 30,
            marginRight: 30,
            padding: 20,
            fontFamily: "Lexend, sans-serif",
            minWidth: 400,
            minHeight: 300,
            maxWidth: 900,
          }}
        >
          <p
            style={{
              fontSize: 18,
              marginTop: 0,
              fontFamily: "Lexend, sans-serif",
              fontWeight: 600,
            }}
          >
            Customer Cluster in your Cloud Account
          </p>
          <Grid container spacing={2} sx={{ width: 600 }}>
            <Box style={{ display: "flex", margin: 16 }}>
              <LogoSelector
                isActive={provisionRequest.cloudProvider === "AWS"}
                src={awsLogo}
                isEnabled={true}
                onClick={() => handleCloudProviderChange("AWS")}
              />
              <LogoSelector
                isActive={provisionRequest.cloudProvider === "AZURE"}
                src={azureLogo}
                isEnabled={true}
                onClick={() => handleCloudProviderChange("AZURE")}
              />
              <LogoSelector
                isActive={provisionRequest.cloudProvider === "GCP"}
                src={gcloudLogo}
                isEnabled={true}
                onClick={() => handleCloudProviderChange("GCP")}
              />
            </Box>
          </Grid>
          {provisionRequest.cloudProvider === "AWS" && getAWSSection()}
          {provisionRequest.cloudProvider === "AZURE" && getAzureSection()}
          {provisionRequest.cloudProvider === "GCP" && getGcpSection()}
        </Paper>
      </Box>
    </>
  );
}
